/* eslint-disable max-len */
/* eslint-disable react/jsx-no-constructed-context-values */
import React, {
  createContext, useContext, useEffect, useState,
} from 'react';
import * as firebase from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  User,
} from 'firebase/auth';
import { useCookies } from 'react-cookie';
import {
  ApolloClient, createHttpLink, InMemoryCache, NormalizedCacheObject,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useDispatch, useSelector } from 'react-redux';
import { AuthContextType } from './AuthContext.types';
import { AuthProviderProps } from './AuthProvider.types';
import { GET_USER_PERMISSIONS, CAN_ACCESS_ENVIRONMENT, GET_ENVIRONMENTS } from '../../graphql';
import {
  AuthSlice,
  resetAuthToken,
  resetClaims,
  resetCurrentUser,
  setAppEnvId,
  setAuthorized,
  setAuthToken,
  setCanAccessEnvironment,
  setClaims,
  setCurrentUser,
  setLoadingCurrentUser,
  setLoginError,
  setUnauthorized,
  setUserPermissions,
} from '../../reducer';
import { EnvironmentType } from '../../types';

const APP_ENV_ID_KEY = `${process.env.REACT_APP_ID}QXBwRW52aXJvbm1lbnRJRA`;

const AuthContext = createContext<AuthContextType>(undefined!);
export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Auth provider documentation.
 * @param apiKey firebase API KEY
 * @param authDomain firebase Authentication Domain.
 * @returns
 */
export function AuthProvider({
  children,
  cache,
}: AuthProviderProps) {
  if (firebase.getApps().length === 0) {
    firebase.initializeApp({
      apiKey: process.env.REACT_APP_CORE_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_CORE_FIREBASE_AUTH_DOMAIN,
      databaseURL: process.env.REACT_APP_CORE_FIREBASE_DATABASE_URL,
      projectId: process.env.REACT_APP_CORE_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_CORE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_CORE_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_CORE_FIREBASE_APP_ID,
      measurementId: process.env.REACT_APP_CORE_FIREBASE_MEASUREMENT_ID,
    });
  }
  const app = firebase.getApp();
  const auth = getAuth(app);

  const dispatch = useDispatch();

  // Fetch all cookies
  const [cookies, setCookie, removeCookie] = useCookies([APP_ENV_ID_KEY]);

  // Initialize State Variables
  const [authorizedUser, setAuthorizedUser] = useState<User | null>();
  const [authClient, setAuthClient] = useState<ApolloClient<NormalizedCacheObject>>();

  // Set selectors to fetch data from redux
  const appEnvId = useSelector((state: { auth: AuthSlice }) => state.auth.appEnvId);
  const appId = useSelector((state: { auth: AuthSlice }) => state.auth.appId);
  const authToken = useSelector((state: { auth: AuthSlice }) => state.auth.authToken);
  const authUrl = useSelector((state: { auth: AuthSlice }) => state.auth.authUrl);
  const canAccessEnvironment = useSelector((state: { auth: AuthSlice }) => state.auth.canAccessEnvironment);
  const claims = useSelector((state: { auth: AuthSlice }) => state.auth.claims);
  const enableAppEnvironment = useSelector((state: { auth: AuthSlice }) => state.auth.enableAppEnvironment);
  const userPermissions = useSelector((state: { auth: AuthSlice }) => state.auth.userPermissions);

  useEffect(() => {
    if (enableAppEnvironment && cookies[APP_ENV_ID_KEY]) {
      dispatch(setAppEnvId(cookies[APP_ENV_ID_KEY]));
    }
  }, []);

  useEffect(() => {
    if (authUrl && appId && authToken) {
      const httpLink = createHttpLink({ uri: authUrl });

      const authorizationHeader = authToken ? { authorization: `Bearer ${authToken}` } : {};
      const appIdHeader = process.env.REACT_APP_ID ? { appid: process.env.REACT_APP_ID } : {};
      const appEnvIdHeader = appEnvId ? { appenvid: appEnvId } : {};

      const authLink = setContext((_, { headers }) => ({
        headers: {
          ...appEnvIdHeader,
          ...appIdHeader,
          ...authorizationHeader,
          ...headers,
        },
      }));

      const client = new ApolloClient({
        cache: cache ?? new InMemoryCache(),
        link: authLink.concat(httpLink),
      });

      client.resetStore();
      setAuthClient(client);
    }
  }, [authUrl, appId, appEnvId, authToken]);

  function login(email: string, password: string) {
    dispatch(setLoadingCurrentUser(true));

    if (!email) {
      dispatch(setLoadingCurrentUser(false));
      dispatch(setLoginError('Login failed! Please enter valid email'));
      return Promise.resolve(null);
    }

    if (!password) {
      dispatch(setLoadingCurrentUser(false));
      dispatch(setLoginError('Login failed! Please enter valid password'));
      return Promise.resolve(null);
    }

    return signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        setAuthorizedUser(userCredential.user);
        dispatch(setLoadingCurrentUser(false));
        return userCredential.user;
      })
      .catch((e) => {
        dispatch(setLoadingCurrentUser(false));
        dispatch(setLoginError('Login failed! Please enter valid username & password'));
        throw e;
      });
  }

  function clear() {
    dispatch(resetAuthToken());
    dispatch(resetClaims());
    dispatch(resetCurrentUser());
    dispatch(setCanAccessEnvironment(false));
    dispatch(setLoadingCurrentUser(false));
    dispatch(setUnauthorized());
    setAuthClient(undefined);
    setAuthorizedUser(undefined);
  }

  function getUserPermissions({ email } : { email: string }) {
    if (enableAppEnvironment && authClient && email) {
      authClient.query({
        query: GET_USER_PERMISSIONS,
        variables: {
          email,
        },
      }).then(
        (result) => {
          dispatch(setUserPermissions(result?.data?.permissions));
        },
      );
    }
  }

  function checkCanAccessEnvironment() {
    if (!enableAppEnvironment) {
      dispatch(setCanAccessEnvironment(true));
      dispatch(setLoadingCurrentUser(true));
      return;
    }

    if (appEnvId) {
      dispatch(setLoadingCurrentUser(true));
      authClient?.query({
        query: CAN_ACCESS_ENVIRONMENT,
      }).then(
        (result) => {
          if (result?.data?.canAccessEnvironment === true) {
            dispatch(setCanAccessEnvironment(true));
            getUserPermissions({ email: authorizedUser?.email || '' });
          } else {
            dispatch(setCanAccessEnvironment(false));
            dispatch(setLoginError('You do not have access to this environment'));
            clear();
          }
        },
      );
    }
  }

  function getEnvironments(variables : { uid: string }) {
    if (authClient && variables.uid) {
      return authClient.query<{ environments: EnvironmentType[] }>({
        query: GET_ENVIRONMENTS,
        variables,
      }).then((result) => result?.data?.environments || []);
    }
    return undefined;
  }

  function logout() {
    clear();
    setAuthClient(undefined);
    return auth.signOut();
  }

  function sendPasswordReset(email: string) {
    dispatch(setLoadingCurrentUser(true));
    return sendPasswordResetEmail(auth, email).then(() => {
      dispatch(setLoadingCurrentUser(false));
    });
  }

  const getUserClaims = () => {
    if (enableAppEnvironment) {
      const permissions: any = {};
      userPermissions?.forEach(
        (permission: { title: string | number; value: any; }) => {
          permissions[permission.title] = permission.value;
        },
      );
      return permissions;
    }
    return claims;
  };

  function hasPermissionToClaim(claim: string) {
    const userClaims = getUserClaims();
    return (userClaims && claim in userClaims && userClaims[claim] === true);
  }

  const setAppEnv = (id: string) => {
    if (id) {
      setCookie(APP_ENV_ID_KEY, id);
      dispatch(setAppEnvId(id));
    }
  };

  const resetAppEnv = () => {
    removeCookie(APP_ENV_ID_KEY);
    dispatch(setAppEnvId(''));
  };

  const loadingCurrentUser = useSelector((state: { auth: AuthSlice }) => state.auth.loadingCurrentUser);

  useEffect(() => {
    const userClaims = getUserClaims();
    if (authorizedUser
      && userClaims
      && authToken
      && canAccessEnvironment
      && loadingCurrentUser
    ) {
      dispatch(setAuthorized());
      dispatch(setLoadingCurrentUser(false));
      dispatch(setCurrentUser({
        displayName: authorizedUser.displayName,
        email: authorizedUser.email,
        uid: authorizedUser.uid,
        admin: userClaims?.admin,
        token: authToken,
      }));

      if (appEnvId) {
        setCookie(APP_ENV_ID_KEY, appEnvId);
      }
      dispatch(setLoginError());
    }
  }, [
    userPermissions,
    canAccessEnvironment,
    authorizedUser,
    claims,
    authToken,
  ]);

  useEffect(() => {
    if (authClient) {
      checkCanAccessEnvironment();
    }
  }, [authClient]);

  useEffect(() => {
    if (authorizedUser) {
      authorizedUser?.getIdTokenResult()?.then((idTokenResult) => {
        dispatch(setAuthToken(idTokenResult.token));
        dispatch(setClaims(idTokenResult.claims));
      });
    }
  }, [authorizedUser]);

  useEffect(() => {
    auth.onAuthStateChanged((user) => {
      if (user) {
        setAuthorizedUser(user);
      } else {
        setAuthorizedUser(undefined);
        dispatch(resetAuthToken());
        dispatch(resetClaims());
      }
      dispatch(setLoadingCurrentUser(false));
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authClient,
        authorizedUser,
        getEnvironments,
        hasPermissionToClaim,
        login,
        logout,
        sendPasswordReset,
        setAppEnv,
        resetAppEnv,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
